import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Flame, BookOpen, Trophy, Sparkles, ArrowRight, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { computeXp, rankOf } from "@/i18n/extra";
import { Star, Medal, Lock } from "lucide-react";

function XpCard({ progress }) {
  const { t } = useI18n();
  const xp = computeXp(progress);
  const r = rankOf(xp);
  const pct = r.next ? Math.round(((xp - r.floor) / (r.next - r.floor)) * 100) : 100;
  return (
    <div className="rounded-3xl bg-[#0E382B] text-white p-7 mb-6 ss-grain overflow-hidden flex flex-col md:flex-row md:items-center gap-6" data-testid="dashboard-xp-card">
      <div className="w-16 h-16 rounded-2xl bg-[#E46A28] flex items-center justify-center shrink-0 animate-float"><Star className="w-8 h-8" /></div>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F6C9AD]">{t("gx.rank")}</div>
        <div className="font-serif text-2xl md:text-3xl font-semibold" data-testid="dashboard-rank">{t(`gx.r${r.index}`)}</div>
        <div className="mt-3 h-2.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-[#E46A28] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
        {r.next && <div className="mt-1.5 text-xs text-white/70">{r.next - xp} XP {t("gx.nextRank")}</div>}
      </div>
      <div className="text-right"><div className="font-serif text-4xl font-semibold" data-testid="dashboard-xp-total">{xp}</div><div className="text-xs text-white/70">{t("gx.xp")}</div></div>
    </div>
  );
}

function Achievements({ progress, lessons }) {
  const { t } = useI18n();
  const done = progress?.completed_lessons || [];
  const scores = Object.values(progress?.quiz_scores || {});
  const a1 = lessons.filter((l) => l.level === "A1");
  const list = [
    ["first", done.length >= 1], ["ten", done.length >= 10],
    ["perfect", scores.some((s) => s.total && s.score === s.total)],
    ["streak", (progress?.longest_streak || 0) >= 3],
    ["words", (progress?.mastered_words || []).length >= 25],
    ["a1", a1.length > 0 && a1.every((l) => done.includes(l.id))],
  ];
  return (
    <div className="rounded-2xl bg-white border border-[#E2DFD7] p-6" data-testid="dashboard-achievements">
      <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-5">{t("gx.achievements")}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {list.map(([k, ok]) => (
          <div key={k} data-testid={`achievement-${k}`} className={`rounded-2xl p-4 border text-center transition-colors ${ok ? "bg-[#FEF3C7] border-[#FCD34D]" : "bg-[#F9F8F5] border-[#E2DFD7] opacity-60"}`}>
            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center ${ok ? "bg-[#E46A28] text-white animate-pop" : "bg-[#EDEBE5] text-[#B5B0A2]"}`}>
              {ok ? <Medal className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
            </div>
            <div className="mt-2 text-sm font-semibold text-[#16201B]">{t(`gx.a_${k}`)}</div>
            <div className="text-[11px] text-[#52635B]">{t(`gx.a_${k}_d`)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, accent, sub }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2DFD7] p-6 hover:-translate-y-1 transition-transform duration-300">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: accent.bg }}>
        <Icon className="w-5 h-5" style={{ color: accent.fg }} />
      </div>
      <div className="font-serif text-3xl font-semibold text-[#16201B]">{value}</div>
      <div className="text-sm text-[#52635B] mt-0.5">{label}</div>
      {sub && <div className="text-xs text-[#B5B0A2] mt-1">{sub}</div>}
    </div>
  );
}

function StreakHeatmap({ dates }) {
  const set = new Set(dates || []);
  const days = [];
  const today = new Date();
  for (let i = 34; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return (
    <div className="grid grid-cols-7 gap-1.5" data-testid="streak-heatmap">
      {days.map((d) => (
        <div key={d} title={d} className="aspect-square rounded-md" style={{ backgroundColor: set.has(d) ? "#0E5A35" : "#EDEBE5" }} />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, login, progress } = useApp();
  const { t } = useI18n();
  const [lessons, setLessons] = useState([]);
  const [totalWords, setTotalWords] = useState(52);

  useEffect(() => {
    api.get("/curriculum").then(({ data }) => setLessons(data));
    api.get("/flashcards").then(({ data }) => setTotalWords(data.length)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#E8F0EC] flex items-center justify-center">
          <Sparkles className="w-8 h-8 text-[#0E382B]" />
        </div>
        <h1 className="mt-6 font-serif text-3xl font-semibold text-[#0E382B]">{t("dash.loginTitle")}</h1>
        <p className="mt-3 text-[#52635B]">{t("dash.loginBody")}</p>
        <Button onClick={login} className="mt-6 rounded-full bg-[#0E382B] hover:bg-[#1C5442] text-white px-7 h-12" data-testid="dashboard-login-button">
          {t("dash.loginBtn")}
        </Button>
      </div>
    );
  }

  const completed = progress?.completed_lessons || [];
  const scores = progress?.quiz_scores || {};
  const mastered = progress?.mastered_words || [];
  const streak = progress?.current_streak || 0;
  const longest = progress?.longest_streak || 0;

  const lvl = (lv) => [lessons.filter((l) => l.level === lv && completed.includes(l.id)).length, lessons.filter((l) => l.level === lv).length];
  const [a1Done, a1Total] = lvl("A1");
  const [a2Done, a2Total] = lvl("A2");
  const [b1Done, b1Total] = lvl("B1");

  const quizList = Object.values(scores);
  const quizPct = quizList.length
    ? Math.round((quizList.reduce((a, s) => a + s.score, 0) / quizList.reduce((a, s) => a + s.total, 0)) * 100)
    : 0;

  const nextLesson = lessons.find((l) => !completed.includes(l.id));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex items-center gap-4 mb-8">
        {user.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full border border-[#E2DFD7]" />}
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#0E382B]">{t("dash.hi")}, {(user.name || "").split(" ")[0]}!</h1>
          <p className="text-[#52635B]">{t("dash.welcome")}</p>
        </div>
      </div>

      <XpCard progress={progress} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Flame} value={streak} label={t("dash.streak")} sub={`${t("dash.longest")}: ${longest}`} accent={{ bg: "#FEF3C7", fg: "#B45309" }} />
        <StatCard icon={BookOpen} value={`${completed.length}/${lessons.length}`} label={t("dash.lessons")} accent={{ bg: "#E8F0EC", fg: "#0E5A35" }} />
        <StatCard icon={Trophy} value={`${quizPct}%`} label={t("dash.accuracy")} sub={`${quizList.length} ${t("dash.quizzesDone")}`} accent={{ bg: "#EDF2FE", fg: "#1E429F" }} />
        <StatCard icon={Award} value={`${mastered.length}/${totalWords}`} label={t("dash.words")} accent={{ bg: "#FDEEE5", fg: "#c9591f" }} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl bg-white border border-[#E2DFD7] p-6">
            <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-5">{t("dash.levelProgress")}</h3>
            <LevelBar label="A1" done={a1Done} total={a1Total} color="#0E5A35" bg="#E8F0EC" />
            <div className="h-4" />
            <LevelBar label="A2" done={a2Done} total={a2Total} color="#1E429F" bg="#EDF2FE" />
            <div className="h-4" />
            <LevelBar label="B1" done={b1Done} total={b1Total} color="#1A6B72" bg="#E3F2F1" />
          </div>
          <Achievements progress={progress} lessons={lessons} />

          {nextLesson ? (
            <Link to={`/lesson/${nextLesson.id}`} data-testid="dashboard-resume-button" className="group flex items-center justify-between rounded-2xl bg-[#0E382B] p-6 hover:bg-[#0b2c22] transition-colors">
              <div>
                <span className="text-xs font-medium text-[#E8F0EC]/70">{t("dash.resumeFrom")}</span>
                <h3 className="font-serif text-xl font-semibold text-white mt-0.5">{nextLesson.title}</h3>
                <p className="text-sm text-[#E8F0EC]/80">{nextLesson.grammarFocus}</p>
              </div>
              <ArrowRight className="w-6 h-6 text-[#E46A28] group-hover:translate-x-1 transition-transform" />
            </Link>
          ) : (
            <div className="rounded-2xl bg-[#E8F0EC] border border-[#B6D4C8] p-6 text-center">
              <Trophy className="w-8 h-8 mx-auto text-[#0E5A35]" />
              <p className="mt-2 font-serif text-lg text-[#0E382B]">{t("dash.allDone")}</p>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-[#E2DFD7] p-6">
          <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-1">{t("dash.calendar")}</h3>
          <p className="text-xs text-[#52635B] mb-4">{t("dash.last5")}</p>
          <StreakHeatmap dates={progress?.activity_dates} />
          <div className="mt-4 flex items-center gap-2 text-xs text-[#52635B]">
            <span className="w-3 h-3 rounded-sm bg-[#EDEBE5]" /> {t("dash.noActivity")}
            <span className="w-3 h-3 rounded-sm bg-[#0E5A35] ml-2" /> {t("dash.practiced")}
          </div>
        </div>
      </div>
    </div>
  );
}

function LevelBar({ label, done, total, color, bg }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-[#16201B]">{label}</span>
        <span className="text-sm text-[#52635B]">{done}/{total}</span>
      </div>
      <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: bg }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
